import { FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Grid } from '@mui/material';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { FieldTableRow } from 'components/_basic/Form/FieldTable2/components/FieldTableRow';
import { IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { useUnitChange } from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useUnitChange';
import { OPERATING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/OperatingActivities/utils/form';
import { OTHER_ITEMS_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OtherItemsForm/utils/form';
import {
  operatingActivitiesPrefix,
  otherItemsPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { NET_INCOME_NAMES, useGetNetIncomeTableHeadings } from './utils/form';
import { FIRST_COLUMN_WIDTH, netIncomePrefix } from '../../../utils/form';
import { getEachRowValues } from '../NetSalesForm/helpers';

import styles from './NetIncomeForm.module.scss';

export const NetIncomeForm: FC<{ disabled: boolean }> = ({ disabled = false }) => {
  const { getValues, setValue } = useFormContext();
  const { key } = useUnitChange();
  const { netIncomeFields, netIncomeLinkedFields, incomeTaxRowTitle, totalRowTitle } = useGetNetIncomeTableHeadings();

  const netIncomeTaxField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.INCOME_TAX);
  const netIncomeProfitField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NET_INCOME);
  const financialExpensesField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.FINANCIAL_EXPENSES);
  const nonOperatingExpensesField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_EXPENSES);
  const nonOperatingIncomeField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_INCOME);

  const interestReceivedField = concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED);
  const interestReceivedIndirectField = concatWithDot(otherItemsPrefix, OTHER_ITEMS_NAMES.INTEREST_RECEIVED);

  const [
    incomeTaxValues,
    incomeProfitValues,
    financialExpensesRowValues,
    nonOperatingExpensesRowValues,
    nonOperatingIncomeValues,
  ] = getValues([
    netIncomeTaxField,
    netIncomeProfitField,
    financialExpensesField,
    nonOperatingExpensesField,
    nonOperatingIncomeField,
  ]);

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const financialIncome = useWatch({ name: concatWithDot(netIncomePrefix, NET_INCOME_NAMES.FINANCIAL_INCOME) });

  const financialIncomeHandler = useCallback((income: IPeriodsTableRow[]) => {
    setValue(interestReceivedField, income[0]);
    setValue(interestReceivedIndirectField, income[0]);
  }, []);

  useDebouncedSave({
    data: financialIncome,
    saveFunction: financialIncomeHandler,
    debounceTime: 500,
    skipExecutionOnFirstRender: true,
  });

  const columnsNames = getPeriodColumnsNames(period);

  return (
    <Grid container spacing={5} direction={'column'}>
      <Grid item xs>
        <FieldTableRow
          rowIndex={0}
          name={concatWithDot(netIncomePrefix, NET_INCOME_NAMES.FINANCIAL_INCOME)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          columnsNames={columnsNames || []}
          rowTitle={netIncomeFields[NET_INCOME_NAMES.FINANCIAL_INCOME].title}
          titleClass={styles.sticky}
          key={key}
          disabled={disabled}
        />
        <ReadOnlyTableRow
          name={financialExpensesField}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          columnValues={getEachRowValues([financialExpensesRowValues], 0, period)}
          rowTitle={netIncomeLinkedFields.financialExpenses.title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={nonOperatingIncomeField}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          columnValues={getEachRowValues(nonOperatingIncomeValues, 0, period)}
          rowTitle={netIncomeFields[NET_INCOME_NAMES.NON_OPERATING_INCOME].title}
          noNeedName={true}
          disabled={disabled}
        />
        <ReadOnlyTableRow
          name={concatWithDot(nonOperatingExpensesField)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          columnValues={getEachRowValues([nonOperatingExpensesRowValues], 0, period)}
          rowTitle={netIncomeLinkedFields.nonOperatingExpenses.title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs>
        <ReadOnlyTableRow
          name={netIncomeTaxField}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          columnValues={getEachRowValues([incomeTaxValues], 0, period)}
          rowTitle={incomeTaxRowTitle}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <TotalRowComponent
          name={netIncomeProfitField}
          columnValues={getEachRowValues([incomeProfitValues], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          totalRowTitle={totalRowTitle}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};
